import {
  Fragment,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { ActivelyHiringBadge } from 'components/ActivelyHiringBadge/ActivelyHiringBadge';
import { Box } from 'components/Box';
import { ButtonBar } from 'components/ButtonBar/ButtonBar';
import { Divider } from 'components/Divider';
import { FormSubmitFunction } from 'components/Form/FormContext';
import { Hide } from 'components/Hide';
import { Icon } from 'components/Icon/Icon';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { BodySmall } from 'components/Text/BodySmall';
import {
  OrgLayoutFooterHolder,
  OrgLayoutFooterWrapper,
  OrgLayoutHeader,
  OrgLayoutSidebarLink,
  OrgLayoutSidebarLinkLi,
  OrgLayoutSidebarLinkUl,
  OrgLayoutSidebarTitle,
} from 'layouts/Org/OrgLayout.styled';
import { ApiOrg } from 'modules/listing/api/org/types/ApiOrg';
import { ListingViewBody } from 'modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewMain } from 'modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSidebar } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSignupCta } from 'modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { orgDashboardApiPartialUpdateOrg } from 'modules/orgDashboard/api/orgDashboardApiOrg';
import { OrgDashboardApiOrg } from 'modules/orgDashboard/api/types/OrgDashboardApiOrg';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { useUserEnvironment } from 'store/hooks/useUserEnvironment';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { identityFilter } from 'utils/functional';
import { setDocumentTitle } from 'utils/meta/setDocumentTitle';
import { scrollIntoView, scrollToHash } from 'utils/scrollIntoView';
import { getSocialNetworkFromLink } from 'utils/social/getSocialNetworkFromLink';
import { iconNameBySocialNetwork } from 'utils/social/iconNameBySocialNetwork';
import { showModal } from 'zustand-stores/modalStore';

import { ViewOrgPageActionButtons } from './ViewOrgPageActionButtons';
import { ViewOrgPageBanner } from './ViewOrgPageBanner';
import { ViewOrgPageDetails } from './ViewOrgPageDetails';
import { ViewOrgPageHeader } from './ViewOrgPageHeader';
import { ViewOrgPageSidebarDetails } from './ViewOrgPageSidebarDetails';
import { ViewOrgPageSidebarHeader } from './ViewOrgPageSidebarHeader';
import { ViewOrgPageSidebarImage } from './ViewOrgPageSidebarImage';
import { ViewOrgPageSidebarTitle } from './ViewOrgPageSidebarTitle';
import { ViewOrgToolbar } from './components/ViewOrgToolbar';
import { trackViewOrgPageView } from './trackViewOrgPageView';

type Props = {
  org: ApiOrg;
  areasOfFocus?: ApiAreaOfFocus[];
  isPreview: boolean;
  isPublished: boolean;
};

export function ViewOrgPage({
  org: orgProp,
  areasOfFocus,
  isPreview,
  isPublished,
}: Props) {
  const [updatedOrg, setUpdatedOrg] = useState<OrgDashboardApiOrg>();
  const org = updatedOrg?.id === orgProp.id ? updatedOrg : orgProp;

  const location = useLocation();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(true);
  const { user } = useUserEnvironment();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: org.id,
  });

  const orgOwner = useMemo(
    () => Boolean(user?.orgs && user.orgs.some((o) => o.id === org.id)),
    [user, org],
  );

  useEffect(() => {
    trackViewOrgPageView(org);
    setDocumentTitle(org.name);

    setTimeout(() => {
      scrollToHash(location.hash.replace('section-', ''));
    }, 1);
  }, [location.hash, org]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (
      queryParams.has('admin-request') &&
      user?.emailVerified &&
      user?.firstName
    ) {
      showModal('ADMIN_REQUEST', {
        objectId: org.id,
        objectName: org.name,
      });
      queryParams.delete('admin-request');
      navigate(`${location.pathname}${queryParams}`);
    }
  }, [
    location.pathname,
    location.search,
    navigate,
    org.id,
    org.name,
    user?.emailVerified,
    user?.firstName,
  ]);

  const onSubmit: FormSubmitFunction<
    Pick<OrgDashboardApiOrg, 'id'> & Partial<OrgDashboardApiOrg>
  > = async ({ values, handleError }) => {
    try {
      setUpdatedOrg(await orgDashboardApiPartialUpdateOrg(values));
    } catch (error) {
      handleError(error as Error);
    }
  };

  const onClickOpportunities = useCallback(
    (
      e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
      analyticsProperties?: Record<string, unknown>,
    ) => {
      trackClicked('See Opportunities', analyticsProperties);

      const element = document.getElementById('opportunities');

      if (element) {
        e.preventDefault();
        scrollIntoView(element);
      }
    },
    [],
  );

  const socialLinks = useMemo(
    () =>
      org.links
        .map((link) => {
          const socialNetwork = getSocialNetworkFromLink(link);
          if (!socialNetwork) return;

          return { link, socialNetwork };
        })
        .filter(identityFilter),
    [org.links],
  );

  return (
    <>
      {orgOwner && (
        <ViewOrgToolbar
          org={org as OrgDashboardApiOrg}
          isPublished={isPublished}
          editMode={editMode}
          setEditMode={setEditMode}
        />
      )}

      {((orgOwner && editMode) || org.banner) && (
        <ViewOrgPageBanner
          org={org}
          isPublished={isPublished}
          isPreview={isPreview}
          orgOwner={orgOwner}
          editMode={editMode}
          onSubmit={onSubmit}
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <BodySmall lineHeight="normal">
            <Hide xs sm md>
              <Box mb="16px">
                <ViewOrgPageSidebarImage
                  org={org}
                  orgOwner={orgOwner}
                  editMode={editMode}
                  onSubmit={onSubmit}
                />
              </Box>
            </Hide>

            {org.hiring && (
              <div style={{ marginBottom: 24 }}>
                <ActivelyHiringBadge />
              </div>
            )}

            <OrgLayoutSidebarTitle aria-hidden="true">
              <Hide xs sm md>
                <ViewOrgPageSidebarTitle org={org} />
              </Hide>
            </OrgLayoutSidebarTitle>

            <OrgLayoutSidebarLinkUl>
              {socialLinks.map(({ socialNetwork, link }) => (
                <OrgLayoutSidebarLinkLi key={link}>
                  <OrgLayoutSidebarLink href={link}>
                    <Icon
                      name={iconNameBySocialNetwork[socialNetwork]}
                      size={16}
                    />
                  </OrgLayoutSidebarLink>
                </OrgLayoutSidebarLinkLi>
              ))}
            </OrgLayoutSidebarLinkUl>

            <Divider marginTop={24} marginBottom={24} />

            <ViewOrgPageSidebarHeader
              org={org}
              onClickOpportunities={onClickOpportunities}
            />

            <ViewOrgPageSidebarDetails org={org} orgOwner={orgOwner} />
          </BodySmall>
        </ListingViewSidebar>

        <ListingViewMain>
          <OrgLayoutHeader>
            {searchContext && (
              <PrintHidden>
                <Box mb={[4, 4, 4, '40px']}>
                  <SearchContextHeader searchContext={searchContext} />
                </Box>
              </PrintHidden>
            )}

            <ViewOrgPageHeader
              org={org}
              onClickOpportunities={onClickOpportunities}
              isPreview={isPreview}
            />
          </OrgLayoutHeader>

          <ViewOrgPageDetails org={org} areasOfFocus={areasOfFocus} />

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!isPreview && !user && (
        <PrintHidden>
          <OrgLayoutFooterHolder>
            <OrgLayoutFooterWrapper>
              <ListingViewSignupCta
                listingUrl={org.url[CURRENT_LOCALE]}
                type="ORG"
              />
            </OrgLayoutFooterWrapper>
          </OrgLayoutFooterHolder>
        </PrintHidden>
      )}

      <ButtonBar>
        <ViewOrgPageActionButtons
          org={org}
          isPreview={isPreview}
          onClickOpportunities={onClickOpportunities}
          mobile
        />
      </ButtonBar>
    </>
  );
}
